import { useState, useEffect } from "react";
import axios from "axios";
import { AICCIMethodologyLink } from "./AICCIMethodology";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const DIM_COLORS = {
  care: "#34d399", diversity: "#ec4899", governance: "#8b5cf6",
  geographic: "#3b82f6", extraction: "#f59e0b", inclusion: "#06b6d4", agency: "#f97316",
};

export const AICCIBanner = ({ entityType }) => {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const url = entityType ? `${API}/aicci?entity_type=${entityType}` : `${API}/aicci`;
    axios.get(url).then(r => {
      if (r.data && r.data.score > 0) setData(r.data);
    }).catch(() => {});
  }, [entityType]);

  if (!data) return null;

  const scoreColor = data.score >= 60 ? "#34d399" : data.score >= 40 ? "#f59e0b" : "#f87171";

  return (
    <div className="rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0c0a1e 0%, #0f1629 50%, #0a1020 100%)", border: "1px solid rgba(139,92,246,0.15)" }} data-testid="aicci-banner">
      <div className="p-5">
        <div className="flex items-start md:items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center flex-shrink-0">
              <svg width="56" height="56" viewBox="0 0 64 64">
                <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="4" />
                <circle cx="32" cy="32" r="28" fill="none" stroke={scoreColor} strokeWidth="4"
                  strokeDasharray={`${data.score * 1.76} ${176 - data.score * 1.76}`}
                  strokeDashoffset="44" strokeLinecap="round" style={{ transition: "stroke-dasharray 1s ease" }} />
              </svg>
              <span className="absolute text-lg font-black" style={{ color: scoreColor }}>{data.score}</span>
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">Conscious Innovation Index</p>
              <p className="text-xs md:text-sm text-slate-300 font-medium leading-snug">{data.interpretation}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <AICCIMethodologyLink entityType={entityType} />
            <button onClick={() => setExpanded(!expanded)} className="text-xs text-violet-400 hover:text-violet-300 font-medium whitespace-nowrap" data-testid="aicci-toggle">
              {expanded ? "Hide details" : "See breakdown \u2192"}
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-5 pt-4 border-t border-white/[0.06] space-y-2.5">
            {Object.entries(data.dimensions).map(([key, dim]) => {
              const refs = { care: "UN SDG 3", diversity: "UN Women/WEF", governance: "OECD", geographic: "UNCTAD", extraction: "EU AI Act", inclusion: "WHO/UNDP", agency: "IEEE EAD" };
              return (
                <div key={key} className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-400 w-28 flex-shrink-0 font-medium">{dim.label}</span>
                  <div className="flex-1 h-2 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${dim.score}%`, background: DIM_COLORS[key] }} />
                  </div>
                  <span className="text-xs font-bold w-10 text-right" style={{ color: DIM_COLORS[key] }}>{dim.score}</span>
                  <span className="text-[8px] text-slate-500 w-20 text-right hidden md:inline">{refs[key]}</span>
                </div>
              );
            })}
            <p className="text-[10px] text-slate-500 pt-2">Informed by <span className="text-slate-400">UN SDGs</span>, <span className="text-slate-400">OECD AI Principles</span>, <span className="text-slate-400">IEEE Ethically Aligned Design</span>, <span className="text-slate-400">WEF Gender Parity</span>, <span className="text-slate-400">EU AI Act</span>, and <span className="text-slate-400">UNCTAD</span> standards. {data.total_apps.toLocaleString()} apps analyzed.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AICCIBanner;
